import React, { useState, useCallback } from "react";

interface MigrationFormat {
  name: string;
  description: string;
  extensions: string[];
  type: string;
  requiredColumns?: string[];
  optionalColumns?: string[];
}

interface ValidationResult {
  valid: boolean;
  error?: string;
  fileSize?: number;
  extension?: string;
  estimatedRecords?: number;
  warnings?: string[];
  sampleData?: {
    headers?: string[];
    rows?: string[][];
  };
}

interface MigrationReport {
  type: string;
  totalProcessed: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
  successRate: number;
  errors: string[];
  warnings: string[];
  hasErrors: boolean;
}

interface Location {
  id: string;
  name: string;
  code: string;
}

export function MigrationPage(): React.ReactElement {
  const [formats, setFormats] = useState<Record<string, MigrationFormat>>({});
  const [selectedType, setSelectedType] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [report, setReport] = useState<MigrationReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>("");

  // Fetch formats on mount
  React.useEffect(() => {
    void fetchFormats();
    void fetchLocations();
  }, []);

  const fetchFormats = async () => {
    try {
      const response = await fetch("/api/migration/formats");
      const data = (await response.json()) as { formats: Record<string, MigrationFormat> };
      setFormats(data.formats);
    } catch (err) {
      console.error("Failed to fetch formats:", err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch("/api/locations");
      const data = (await response.json()) as { member?: Location[] };
      setLocations(data.member ?? []);
    } catch (err) {
      console.error("Failed to fetch locations:", err);
    }
  };

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setValidation(null);
      setReport(null);
      setError(null);
    }
  }, []);

  const handleValidate = async () => {
    if (!file || !selectedType) return;

    setIsValidating(true);
    setError(null);
    setValidation(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", selectedType);

      const response = await fetch("/api/migration/validate", {
        method: "POST",
        body: formData,
      });

      const data = (await response.json()) as ValidationResult;
      setValidation(data);
    } catch (err) {
      setError("Erreur lors de la validation");
      console.error("Validation error:", err);
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!file || !selectedType) return;

    setIsImporting(true);
    setError(null);
    setReport(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (selectedLocation) {
        formData.append("location_id", selectedLocation);
      }

      const endpoint = getImportEndpoint(selectedType);
      const response = await fetch(endpoint, {
        method: "POST",
        body: formData,
      });

      const data = (await response.json()) as {
        success: boolean;
        report?: MigrationReport;
        error?: string;
      };

      if (data.success && data.report) {
        setReport(data.report);
      } else {
        setError(data.error ?? "Erreur lors de l'import");
      }
    } catch (err) {
      setError("Erreur lors de l'import");
      console.error("Import error:", err);
    } finally {
      setIsImporting(false);
    }
  };

  const getImportEndpoint = (type: string): string => {
    switch (type) {
      case "unimarc":
      case "marcxml":
        return "/api/migration/import/unimarc";
      case "items_csv":
        return "/api/migration/import/items";
      case "patrons_csv":
        return "/api/migration/import/patrons";
      default:
        return "/api/migration/import/unimarc";
    }
  };

  const needsLocation = selectedType === "items_csv" || selectedType === "patrons_csv";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white shadow dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Migration de données</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Importez des données depuis votre ancien SIGB
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left: Import Form */}
          <div className="lg:col-span-2">
            <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
              <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                Importer des données
              </h2>

              {/* Format selection */}
              <div className="mb-4">
                <label
                  htmlFor="format"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Type de fichier
                </label>
                <select
                  id="format"
                  value={selectedType}
                  onChange={(e) => {
                    setSelectedType(e.target.value);
                    setValidation(null);
                    setReport(null);
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Sélectionnez un format...</option>
                  {Object.entries(formats).map(([key, format]) => (
                    <option key={key} value={key}>
                      {format.name} - {format.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Location selection (for items and patrons) */}
              {needsLocation && (
                <div className="mb-4">
                  <label
                    htmlFor="location"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Établissement par défaut
                  </label>
                  <select
                    id="location"
                    value={selectedLocation}
                    onChange={(e) => {
                      setSelectedLocation(e.target.value);
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Sélectionnez un établissement...</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name} ({loc.code})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* File upload */}
              <div className="mb-4">
                <label
                  htmlFor="file"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Fichier à importer
                </label>
                <div className="mt-1 flex items-center justify-center rounded-md border-2 border-dashed border-gray-300 px-6 py-10 dark:border-gray-600">
                  <div className="text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="mt-4 flex text-sm text-gray-600 dark:text-gray-400">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400"
                      >
                        <span>Sélectionner un fichier</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          onChange={handleFileChange}
                          accept={
                            selectedType && formats[selectedType]
                              ? formats[selectedType].extensions.map((e) => "." + e).join(",")
                              : undefined
                          }
                        />
                      </label>
                      <p className="pl-1">ou glisser-déposer</p>
                    </div>
                    {file && (
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        {file.name} ({(file.size / 1024).toFixed(1)} Ko)
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => void handleValidate()}
                  disabled={!file || !selectedType || isValidating}
                  className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  {isValidating ? "Validation..." : "Valider le fichier"}
                </button>
                <button
                  type="button"
                  onClick={() => void handleImport()}
                  disabled={
                    !file || !selectedType || isImporting || (needsLocation && !selectedLocation)
                  }
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {isImporting ? "Import en cours..." : "Importer"}
                </button>
              </div>

              {/* Error message */}
              {error && (
                <div className="mt-4 rounded-md bg-red-50 p-4 dark:bg-red-900/30">
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}
            </div>

            {/* Validation results */}
            {validation && (
              <div className="mt-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800">
                <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                  Résultats de la validation
                </h3>

                <div
                  className={`rounded-md p-4 ${
                    validation.valid
                      ? "bg-green-50 dark:bg-green-900/30"
                      : "bg-red-50 dark:bg-red-900/30"
                  }`}
                >
                  <p
                    className={`font-medium ${
                      validation.valid
                        ? "text-green-800 dark:text-green-200"
                        : "text-red-800 dark:text-red-200"
                    }`}
                  >
                    {validation.valid ? "✓ Fichier valide" : "✗ Fichier invalide"}
                  </p>
                  {validation.error && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-300">
                      {validation.error}
                    </p>
                  )}
                </div>

                {validation.valid && (
                  <dl className="mt-4 space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500 dark:text-gray-400">
                        Enregistrements estimés
                      </dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">
                        {validation.estimatedRecords?.toLocaleString()}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500 dark:text-gray-400">
                        Taille du fichier
                      </dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">
                        {validation.fileSize
                          ? (validation.fileSize / 1024).toFixed(1) + " Ko"
                          : "-"}
                      </dd>
                    </div>
                  </dl>
                )}

                {validation.warnings && validation.warnings.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Avertissements
                    </h4>
                    <ul className="mt-2 list-inside list-disc text-sm text-yellow-700 dark:text-yellow-300">
                      {validation.warnings.map((warning, idx) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {validation.sampleData?.headers && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Colonnes détectées
                    </h4>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {validation.sampleData.headers.map((header, idx) => (
                        <span
                          key={idx}
                          className="rounded bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-700"
                        >
                          {header}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Import report */}
            {report && (
              <div className="mt-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800">
                <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                  Rapport d'import
                </h3>

                <div className="grid gap-4 sm:grid-cols-4">
                  <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/30">
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                      {report.totalProcessed.toLocaleString()}
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">Total traités</p>
                  </div>
                  <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/30">
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {report.successCount.toLocaleString()}
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300">Importés</p>
                  </div>
                  <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/30">
                    <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                      {report.skippedCount.toLocaleString()}
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">Ignorés</p>
                  </div>
                  <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/30">
                    <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                      {report.errorCount.toLocaleString()}
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300">Erreurs</p>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Taux de succès</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {report.successRate.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className="h-full bg-green-500"
                      style={{ width: `${String(report.successRate)}%` }}
                    />
                  </div>
                </div>

                {report.errors.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                      Erreurs ({report.errors.length})
                    </h4>
                    <ul className="mt-2 max-h-40 overflow-y-auto text-sm text-red-700 dark:text-red-300">
                      {report.errors.slice(0, 20).map((err, idx) => (
                        <li key={idx} className="border-b border-red-100 py-1 dark:border-red-800">
                          {err}
                        </li>
                      ))}
                      {report.errors.length > 20 && (
                        <li className="py-1 italic">
                          ... et {report.errors.length - 20} autres erreurs
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Help */}
          <div className="lg:col-span-1">
            <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
              <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                Guide de migration
              </h2>

              <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    1. Exporter depuis votre SIGB
                  </h3>
                  <p>
                    Exportez vos données au format UNIMARC ISO 2709 pour les notices, ou CSV pour
                    les exemplaires et lecteurs.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    2. Valider le fichier
                  </h3>
                  <p>
                    Utilisez la fonction de validation pour vérifier que le fichier est lisible et
                    contient les colonnes attendues.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">3. Importer</h3>
                  <p>
                    Lancez l'import. Les doublons (même code-barres) seront automatiquement ignorés.
                  </p>
                </div>

                <div className="rounded-md bg-yellow-50 p-3 dark:bg-yellow-900/30">
                  <p className="text-yellow-800 dark:text-yellow-200">
                    <strong>Conseil :</strong> Commencez par importer les notices bibliographiques,
                    puis les exemplaires (qui doivent être liés aux notices), et enfin les lecteurs.
                  </p>
                </div>
              </div>
            </div>

            {/* Format details */}
            {selectedType && formats[selectedType] && (
              <div className="mt-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800">
                <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                  {formats[selectedType].name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {formats[selectedType].description}
                </p>

                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                    Extensions acceptées
                  </p>
                  <div className="mt-1 flex gap-1">
                    {formats[selectedType].extensions.map((ext) => (
                      <span
                        key={ext}
                        className="rounded bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-700"
                      >
                        .{ext}
                      </span>
                    ))}
                  </div>
                </div>

                {formats[selectedType].requiredColumns && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      Colonnes requises
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {formats[selectedType].requiredColumns.map((col) => (
                        <span
                          key={col}
                          className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800 dark:bg-red-900 dark:text-red-200"
                        >
                          {col}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {formats[selectedType].optionalColumns && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      Colonnes optionnelles
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {formats[selectedType].optionalColumns.map((col) => (
                        <span
                          key={col}
                          className="rounded bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-700"
                        >
                          {col}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default MigrationPage;
